import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyOrderKey,
  ShopifyRequestedOrderEditKey,
  ShopifyRequestedOrderEditLineItemKey,
  ShopifyRequestedOrderEditLineItemToRequestedOrderEditKey,
  ShopifyRequestedOrderEditToLineItemsKey,
  ShopifyRequestedOrderEditToOrderKey,
  ShopifyRequestedOrderEditToShopKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyRequestedOrderEditKey,
  "2026-10": {
    name: "Shopify Requested Order Edit",
    graphQLTypename: "RequestedOrderEdit",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "id",
            shopifyFieldKey: "id",
            availableInWebhook: ["requested_order_edit/created", "requested_order_edit/resolved", "requested_order_edit/declined"],
          },
        },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "status",
            shopifyFieldKey: "status",
            availableInWebhook: ["requested_order_edit/created", "requested_order_edit/resolved", "requested_order_edit/declined"],
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "declined" }, { name: "requested" }, { name: "resolved" }],
        },
      },
      {
        name: "Requested At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "requestedAt",
            shopifyFieldKey: "requested_at",
            availableInWebhook: ["requested_order_edit/created", "requested_order_edit/resolved", "requested_order_edit/declined"],
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "createdAt",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "updatedAt",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Request Declined At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "requestDeclinedAt",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Request Resolved At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "requestResolvedAt",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyRequestedOrderEditToOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "order",
            shopifyFieldKey: "order_id",
            availableInWebhook: ["requested_order_edit/created", "requested_order_edit/resolved", "requested_order_edit/declined"],
            graphQLSelection: "id",
          },
        },
        configuration: { relatedModelKey: ShopifyOrderKey },
      },
      {
        name: "Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyRequestedOrderEditToLineItemsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "lineItems",
            shopifyFieldKey: "line_items",
            skipGraphQLSelection: true,
            availableInWebhook: {
              "requested_order_edit/created": "line_items.removals",
              "requested_order_edit/resolved": "line_items.removals",
              "requested_order_edit/declined": "line_items.removals",
            },
          },
        },
        configuration: {
          relatedModelKey: ShopifyRequestedOrderEditLineItemKey,
          inverseFieldKey: ShopifyRequestedOrderEditLineItemToRequestedOrderEditKey,
          dependent: "delete",
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyRequestedOrderEditToShopKey,
        configuration: { relatedModelKey: ShopifyShopKey },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "orders",
      resource: "order.requestedOrderEdits",
    },
  },
};

export const ShopifyRequestedOrderEdit = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
